/**
 * What a tool page does with a dropped video (SEO_BRIEF_04 item 7).
 *
 *   7a  Read the subtitle track LIST from the head of the file, ask which track when there is a
 *       choice, take the picked TEXT track out in the worker and hand it to the page as a parsed
 *       file. An IMAGE track goes to the OCR hook (`lib/image-subtitles.ts`) or, with none
 *       registered, gets an honest sentence. The video is read in the browser and never sent.
 *   7b  Transcribe the speech: copy the audio track out in the media worker (`lib/media.ts`) and
 *       offer THAT through the page's own B6 upload, so the limits, the minutes and the job page
 *       are B6's. Offered when the video has no subtitles inside (B5_NO_EMBEDDED_TRACK's button),
 *       and first on the transcription pages (/mp4-to-srt, /srt-generator, /mkv-to-srt) when the
 *       video carries no text or picture subtitles; when it does, those are offered first.
 *
 * The page keeps everything it already does: this module never touches its documents, only
 * calls `land` with a parsed track, exactly the `ParsedInfo` a dropped subtitle file produces.
 * Every word it shows is in the page's templates (`[data-video-labels]`, the track picker, and
 * the state bank), so there is no copy here, and a translated page gets its own words. A track's
 * language is named by `Intl` in the page's language.
 */

import type {
  ContainerProbePayload,
  ContainerTrackInfo,
  ExtractedTrackInfo,
} from '../../../../../src/core/protocol.ts';
import { qs } from './dom.ts';
import { soundOnlyVideo } from './dropzone.ts';
import { shortFilename } from './format.ts';
import { pageLang } from './i18n.ts';
import { handoverOf, imageSubtitleHandler } from './image-subtitles.ts';
import { audioForUpload } from './media.ts';
import { type SlotValues, renderState, showState } from './states.ts';
import { mountTrackPicker, type PickerHandle, pickerModel } from './track-picker.ts';
import { capMegabytes } from './upload.ts';
import { familyOf, longerHead, videoHead } from './video-head.ts';
import { type SubtitleWorkerClient, WorkerFailure } from './worker.ts';

/** `extract`: every converter page and /extract-subtitles. `transcribe`: the speech pages. */
export type VideoMode = 'extract' | 'transcribe';

export interface VideoFlowDeps {
  readonly client: SubtitleWorkerClient;
  readonly region: HTMLElement;
  readonly mode: VideoMode;
  /** The line under the drop zone. Null clears it. */
  readonly status: (message: string | null) => void;
  /**
   * The page's B6, with B6_VIDEO's banner and values instead of B6's. `video` is the file the
   * audio came out of, for the usage counter.
   */
  readonly offerUpload: (
    audio: File,
    banner: { state: string; values: SlotValues },
    video: File,
  ) => void;
  /** The upload byte cap (`/api/platforms`), or null when it could not be learned. */
  readonly uploadCap: () => Promise<number | null>;
  /** A failure the page shows its own way (a version mismatch, a crashed worker). */
  readonly onError: (err: unknown) => void;
  /**
   * A video whose subtitle tracks can't be read here but whose sound could be (MTS, M2TS, OGV,
   * lib/dropzone.ts `soundOnlyVideo`), on a page that only reads tracks: the page's
   * B5_VIDEO_FORMAT, counted.
   */
  readonly refuseVideo: (file: File) => void;
}

export interface IngestOptions {
  /** Keep the reassembled subtitle file in the worker, as a dropped file's bytes are kept. */
  readonly retainBytes: boolean;
  /** True once a newer drop has superseded this one. */
  readonly stale: () => boolean;
  /**
   * The page takes a parsed track. Called once per pick: picking another track from the list
   * that stays on screen calls it again with that one.
   */
  readonly land: (info: ExtractedTrackInfo, file: File) => Promise<void>;
  /** Take the first text track picked and take the picker down (a merge page's list). */
  readonly once?: boolean;
}

export interface VideoFlow {
  /** A dropped video. Resolves when the flow has finished with it, whichever way. */
  ingest(file: File, options: IngestOptions): Promise<void>;
  /** Make subtitles from the video's speech: take the audio out and offer the upload. */
  transcribe(file: File): Promise<void>;
  /** A newer drop arrived: stop waiting for a pick or a choice, and stop any audio copy. */
  abandon(): void;
}

/** The page's own words for the moments between banners: `template[data-video-labels]`. */
function label(key: string): string {
  const bank = qs<HTMLTemplateElement>('template[data-video-labels]');
  const node = bank?.content.querySelector(`[data-video-label="${key}"]`);
  return node?.textContent?.trim() ?? '';
}

/** Above this the whole-file read a text track needs is refused up front, rather than tried. */
const READ_LIMIT = 2 * 1024 * 1024 * 1024;

export function createVideoFlow(deps: VideoFlowDeps): VideoFlow {
  const { client, region } = deps;
  /** Resolves whatever the current flow is waiting on with null. */
  let release: (() => void) | null = null;
  let audioAbort: AbortController | null = null;
  let generation = 0;

  /** Waits for one of `actions` to be clicked in the region, or for `abandon`. */
  function waitForAction(actions: readonly string[]): Promise<string | null> {
    return new Promise((resolve) => {
      const onClick = (ev: Event) => {
        const target = ev.target instanceof Element ? ev.target.closest('[data-action]') : null;
        const action = target?.getAttribute('data-action') ?? '';
        if (!actions.includes(action)) return;
        done(action);
      };
      const done = (value: string | null) => {
        region.removeEventListener('click', onClick);
        release = null;
        resolve(value);
      };
      region.addEventListener('click', onClick);
      release = () => done(null);
    });
  }

  /** The track list, from as little of the file as will hold it. */
  async function probe(file: File): Promise<ContainerProbePayload> {
    const family = familyOf(new Uint8Array(await file.slice(0, 16).arrayBuffer()));
    let head = await videoHead(file, family);
    for (;;) {
      const had = head.bytes.length;
      try {
        return await client.request('probeContainer', {
          inputName: file.name,
          bytes: head.bytes,
          partial: head.partial,
        });
      } catch (err) {
        if (!(err instanceof WorkerFailure) || err.reason !== 'incomplete' || !head.partial) {
          throw err;
        }
        const next = await longerHead(file, had);
        // Past the head limit: the whole file, once. A track list that late is rare enough.
        head = next ?? { bytes: new Uint8Array(await file.arrayBuffer()), partial: false };
      }
    }
  }

  /** The demuxer's sentence for a person, from a failure's detail. */
  function sentenceOf(err: WorkerFailure): string {
    const detail = err.detail as { detail?: unknown } | null;
    return typeof detail?.detail === 'string' ? detail.detail : label('unreadable');
  }

  async function wholeFile(file: File): Promise<Uint8Array | null> {
    if (file.size > READ_LIMIT) return null;
    try {
      return new Uint8Array(await file.arrayBuffer());
    } catch {
      // NotReadableError, or a RangeError on a buffer this browser will not allocate.
      return null;
    }
  }

  /** One text track out of the video, parsed. Null when it could not be (the banner is up). */
  async function extractText(
    file: File,
    track: ContainerTrackInfo,
    options: IngestOptions,
  ): Promise<ExtractedTrackInfo | null> {
    deps.status(label('extracting'));
    try {
      const bytes = await wholeFile(file);
      if (bytes === null) {
        showState(region, 'B5_VIDEO_UNREADABLE', { detail: label('too-large') });
        return null;
      }
      return await client.request('extractTrack', {
        inputName: file.name,
        bytes,
        trackIndex: track.index,
        retainRaw: false,
        retainBytes: options.retainBytes,
      });
    } catch (err) {
      if (err instanceof WorkerFailure && (err.reason === 'no-cues' || err.reason === 'empty')) {
        showState(region, 'B5_EMPTY', {});
        return null;
      }
      if (err instanceof WorkerFailure) {
        showState(region, 'B5_VIDEO_UNREADABLE', { detail: sentenceOf(err) });
        return null;
      }
      deps.onError(err);
      return null;
    } finally {
      deps.status(null);
    }
  }

  /** An image track: to the OCR hook when one is registered. True when it took over. */
  async function imageTrack(file: File, track: ContainerTrackInfo): Promise<boolean> {
    const handler = imageSubtitleHandler();
    if (handler === null) return false;
    deps.status(label('extracting'));
    try {
      const bytes = await wholeFile(file);
      if (bytes === null) return false;
      const payload = await client.request('imageTrack', {
        inputName: file.name,
        bytes,
        trackIndex: track.index,
      });
      const handover = handoverOf(payload, track, file.name);
      return handover === null ? false : await handler(handover);
    } catch {
      return false;
    } finally {
      deps.status(null);
    }
  }

  /**
   * The B5_IMAGE_TRACK banner, as a node, for the picker's note or the region. It keeps the
   * sentence naming the OCR page for the track's family (`pgs` or `vobsub`); a DVB track keeps
   * neither, since neither page reads it.
   */
  function imageTrackNotice(track: ContainerTrackInfo): DocumentFragment | null {
    const family = track.family === 'pgs' || track.family === 'vobsub' ? track.family : null;
    return renderState('B5_IMAGE_TRACK', family ? { [family]: '1' } : {});
  }

  async function ingest(file: File, options: IngestOptions): Promise<void> {
    abandon();
    const mine = ++generation;
    const stale = () => mine !== generation || options.stale();

    deps.status(label('reading'));
    let tracks: readonly ContainerTrackInfo[];
    try {
      tracks = (await probe(file)).tracks;
    } catch (err) {
      deps.status(null);
      if (stale()) return;
      if (!(err instanceof WorkerFailure)) {
        deps.onError(err);
        return;
      }
      // Not a container the subtitle reader takes (AVI, TS). Its sound may still be readable.
      if (deps.mode === 'transcribe') return transcribe(file);
      // MTS, M2TS and OGV have no sentence of the demuxer's own, which would call them "not a
      // video container"; the page names the format and the ones that work.
      if (err.reason === 'unsupported-container' && soundOnlyVideo(file.name)) {
        deps.refuseVideo(file);
        return;
      }
      showState(region, 'B5_VIDEO_UNREADABLE', { detail: sentenceOf(err) });
      return;
    }
    deps.status(null);
    if (stale()) return;

    // Text tracks, and picture tracks (PGS, VobSub) that the OCR pages read: both are subtitles
    // a visitor can take out in the browser without spending minutes.
    const readableCount = tracks.filter((t) => t.kind === 'text' || t.kind === 'image').length;
    if (deps.mode === 'transcribe') {
      // The speech pages: transcription is the job, unless the video already has subtitles,
      // which the page's copy says are faster to take out and cost no minutes. A disc rip's
      // MKV often carries only picture subtitles; /mkv-to-srt's copy says those are listed
      // too, and a picked one goes to /sup-to-srt or /idx-sub-to-srt (SEO_BRIEF_06 3.2).
      if (readableCount === 0) return transcribe(file);
      showState(region, 'VIDEO_HAS_SUBTITLES', {});
      const choice = await waitForAction(['video-extract', 'video-transcribe']);
      if (choice === null || stale()) return;
      if (choice === 'video-transcribe') return transcribe(file);
      region.replaceChildren();
    }

    if (tracks.length === 0) {
      showState(region, 'B5_NO_EMBEDDED_TRACK', { filename: shortFilename(file.name) });
      const choice = await waitForAction(['transcribe-instead']);
      if (choice !== null && !stale()) await transcribe(file);
      return;
    }

    const model = pickerModel(tracks, label('track'), pageLang());
    if (model.textCount === 0 && model.imageCount === 0) {
      // Only formats nothing here reads (TTML, Kate): a list with nothing to pick is no help.
      showState(region, 'B5_VIDEO_UNREADABLE', { detail: label('unsupported-track') });
      return;
    }
    if (model.auto !== null) {
      const track = model.auto;
      if (track.kind === 'text') {
        const info = await extractText(file, track, options);
        if (info !== null && !stale()) await options.land(info, file);
      } else if (!(await imageTrack(file, track)) && !stale()) {
        const notice = imageTrackNotice(track);
        if (notice) region.replaceChildren(notice);
      }
      return;
    }

    // A choice: the list goes up, and stays, so another track can be picked after the first.
    let resolvePick: ((track: ContainerTrackInfo | null) => void) | null = null;
    let picker: PickerHandle | null = null;
    const nextPick = () =>
      new Promise<ContainerTrackInfo | null>((resolve) => {
        resolvePick = resolve;
        release = () => resolve(null);
      });
    picker = mountTrackPicker(region, model, (track) => resolvePick?.(track));
    if (picker === null) return;
    region.focus({ preventScroll: false });
    for (;;) {
      const track = await nextPick();
      release = null;
      if (track === null || stale()) return;
      picker.note(null);
      picker.busy(true);
      if (track.kind === 'text') {
        const info = await extractText(file, track, options);
        picker.busy(false);
        if (stale()) return;
        if (info === null) return; // its banner replaced the list
        await options.land(info, file);
        if (options.once) {
          region.replaceChildren();
          return;
        }
      } else {
        const taken = await imageTrack(file, track);
        picker.busy(false);
        if (taken || stale()) return;
        picker.note(imageTrackNotice(track));
      }
    }
  }

  async function transcribe(file: File): Promise<void> {
    audioAbort?.abort();
    const controller = new AbortController();
    audioAbort = controller;
    const mine = generation;
    const working = label('audio');
    deps.status(working);
    try {
      const cap = await deps.uploadCap();
      const audio = await audioForUpload(file, {
        maxBytes: cap,
        signal: controller.signal,
        onProgress: (fraction) => deps.status(`${working} ${Math.floor(fraction * 100)}%`),
      });
      if (controller.signal.aborted || mine !== generation) return;
      if (!audio.ok) {
        if (audio.reason === 'cancelled') return;
        console.info('[video] the audio could not be taken out', audio);
        const reason = audio.reason === 'too-large' ? 'too_large' : audio.reason.replace('-', '_');
        showState(region, 'VIDEO_NO_AUDIO', { [reason]: '1', limit_mb: capMegabytes(cap) });
        return;
      }
      deps.offerUpload(
        audio.file,
        {
          state: 'B6_VIDEO',
          values: { filename: shortFilename(file.name) },
        },
        file,
      );
    } finally {
      if (audioAbort === controller) audioAbort = null;
      deps.status(null);
    }
  }

  function abandon(): void {
    generation += 1;
    release?.();
    release = null;
    audioAbort?.abort();
    audioAbort = null;
  }

  return { ingest, transcribe, abandon };
}
